import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useReviewStatsQuery from '../../../components/api/queries/useReviewStatsQuery';
import VerifiedBlueIcon from '../my-page-test-icon/VerifiedBlueIcon';
import { getMannerSignalColor } from '../utils/getMannerSignalColor';
import MyPageMenuList from './MyPageMenuList';
import ProfileAvatarSection from './ProfileAvatarSection';
import ProfileNickname from './ProfileNickname';
import RoundedCard from './RoundedCard';
import UpdateUserProfileBottomSheet from './UpdateUserProfileBottomSheet';
import AddUserProfileV1BottomSheet from '../../../components/bottomSheet/AddUserProfileV1BottomSheet';
import MannerSignal from './MannerSignal';
import type { UserDataType } from '../../types/UserType';
import { t } from 'i18next';

interface TradingProfileCardProps {
  user: UserDataType;
  showTradingProfile: boolean;
  setIsShowTradingProfileBottomSheet: (isShow: boolean) => void;
}

const TradingProfileCard = ({
  user,
  showTradingProfile,
  setIsShowTradingProfileBottomSheet
}: TradingProfileCardProps) => {
  const tradingProfileMenu = [
    {
      title: 'myPage.myPageMenuItem.manageTradingPhotoCards',
      path: '/my-trading-photo-cards'
    },
    {
      title: 'myPage.myPageMenuItem.manageTradingHistory',
      path: '/my-photo-cards-market'
    },
    {
      title: 'myPage.myPageMenuItem.manageReviews',
      path: `/trading_photo_cards/reviews?userProfileId=${user?.tradingUserProfile?.userProfileId}`
    },
    {
      title: showTradingProfile
        ? 'myPage.myPageMenuItem.hideSocialProfile'
        : 'myPage.myPageMenuItem.manageSocialProfile',
      path: '',
      onClick: () => setIsShowTradingProfileBottomSheet(true)
    }
  ];

  const navigate = useNavigate();
  const [isShowBottomSheet, setIsShowBottomSheet] = useState(false);

  const reviewStats = useReviewStatsQuery({
    userProfileId: user?.tradingUserProfile?.userProfileId
  });

  const handleEditNickname = () => {
    setIsShowBottomSheet(true);
  };

  const mannerSignalColor = getMannerSignalColor(reviewStats?.ratingsScore);
  const handleNavigateToProfile = () => {
    if (user.tradingUserProfile) {
      navigate(`/trading/user_profile/${user.tradingUserProfile?.userProfileId}`);
    } else {
      setIsShowBottomSheet(true);
    }
  };

  return (
    <>
      <RoundedCard>
        <div className="px-[20px] pb-[12px]">
          <div className="flex pt-[20px] pb-[6px] gap-[12px] items-center">
            <ProfileAvatarSection
              thumbnailUrl={user.tradingUserProfile?.thumbnailUrl}
              userProfileId={user.tradingUserProfile?.userProfileId}
              nickname={user.tradingUserProfile?.nickname}
              description={user.tradingUserProfile?.description}
              onCreateProfile={() => setIsShowBottomSheet(true)}
            />
            <div className="flex justify-between w-full items-center">
              <div className="flex flex-col gap-[8px]">
                <ProfileNickname
                  nickname={user.tradingUserProfile?.nickname}
                  onEditClick={handleEditNickname}
                />
                <MannerSignal mannerSignalColor={mannerSignalColor} />
              </div>
              {user.tradingUserProfile?.identified ? (
                <button
                  type="button"
                  className="bg-[var(--verified,rgba(80,176,248,0.10))] dark:bg-[#33465A] rounded-[4px] px-[7px] py-[4px] h-fit flex items-center gap-[2px]"
                  onClick={handleNavigateToProfile}
                >
                  <p className="text-verified text-[11px] font-semibold">
                    {t('myPage.identityVerifiedTitle')}
                  </p>
                  <VerifiedBlueIcon />
                </button>
              ) : (
                <button
                  type="button"
                  className="bg-warningContainer dark:bg-[#492127] rounded-[4px] px-[7px] py-[4px] h-fit"
                  onClick={handleNavigateToProfile}
                >
                  <p className="text-warning dark:text-[#FF495A] text-[11px] font-semibold">
                    {t('myPage.identityVerificationNeededTitle')}
                  </p>
                </button>
              )}
            </div>
          </div>
          <MyPageMenuList items={tradingProfileMenu} />
        </div>
      </RoundedCard>

      {user.tradingUserProfile ? (
        <UpdateUserProfileBottomSheet
          title="tradingProfileNicknameChangeTitle"
          userProfile={user.tradingUserProfile}
          isShowProfileBottomSheet={isShowBottomSheet}
          setIsShowProfileBottomSheet={setIsShowBottomSheet}
        />
      ) : (
        <AddUserProfileV1BottomSheet
          title={t('tradingPhotoCardPage.addProfileBottomSheetTitle')}
          category="trading"
          userProfile={user.tradingUserProfile}
          isShowProfileBottomSheet={isShowBottomSheet}
          setIsShowProfileBottomSheet={setIsShowBottomSheet}
          onCompleted={() => {}}
        />
      )}
    </>
  );
};

export default TradingProfileCard;
